import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import type {AiRedesignPromptBoxProps, EditableComponentsMap, RedesignIssue, SelectedPromptOptionValues} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import {CreditsPillTheme, RedesignIssueType} from '@Components/ai-resdesign-prompt-box/ai-redesign-prompt-box.types';
import styles from './ai-redesign-prompt-box.module.scss';
import {AiRedesignPromptBoxIssue, getDefaultPromptOptionValue, getPromptOptionsToUse} from '@Libraries/ai-redesign-prompt-box-library';
import {AiRedesignPromptSuggestions} from '@Components/ai-resdesign-prompt-box/components/ai-redesign-prompt-suggestions';
import {PromptBoxHeader} from '@Components/ai-resdesign-prompt-box/components/prompt-box-header';
import {InputBoxFooter} from '@Components/ai-resdesign-prompt-box/components/input-box-footer';
import {PromptInputField} from '@Components/ai-resdesign-prompt-box/components/prompt-input-field/prompt-input-field';
import {AiRedesignIssue} from '@Components/ai-resdesign-prompt-box/components/ai-redesign-issue';
import {AnimatePresence} from "framer-motion";

export function AiRedesignPromptBox({
  promptBoxClasses = '',
  isPromptBoxSelectedOnRender = false,
  isPromptBoxLoading = false,
  unselectOnOutsideClick = false,
  ...props
}: AiRedesignPromptBoxProps): ReactElement {
  const getSelectedPromptOptionValues = (): SelectedPromptOptionValues => {
    if (props.defaultSelectedPromptOptionValues) {
      return props.defaultSelectedPromptOptionValues;
    }

    const selectedPromptOptionValues: SelectedPromptOptionValues = {} as SelectedPromptOptionValues;
    promptOptionsToUse.forEach((option) => {
      selectedPromptOptionValues[option] = getDefaultPromptOptionValue(option);
    });

    return selectedPromptOptionValues;
  };

  const promptBoxRef = useRef<HTMLDivElement>(null);
  const [editableComponentsMap, setEditableComponentsMap] = useState(props.editableComponentsMap);
  const promptOptionsToUse = getPromptOptionsToUse(editableComponentsMap, props.promptOptionsForEditableComponents);
  const [isPromptBoxSelected, setIsPromptBoxSelected] = useState(isPromptBoxSelectedOnRender);
  const [prompt, setPrompt] = useState(props.defaultPrompt ?? '');
  const [selectedPromptOptionValues, setSelectedPromptOptionValues] = useState<SelectedPromptOptionValues>(getSelectedPromptOptionValues());
  const [isLoading, setIsLoading] = useState<boolean>(isPromptBoxLoading);
  const [redesignIssue, setRedesignIssue] = useState(props.redesignIssueOnRender);

  // selfCodeReview: replace the useState and useEffect for redesignIssue with a use ref
  useEffect(() => {
    setRedesignIssue(props.redesignIssueOnRender);
  }, [props.redesignIssueOnRender]);

  const handlePromptChange = (prompt: string): void => {
    setPrompt(prompt);
    props.onPromptChange?.(prompt);
  };

  const updateSelectedPromptOptionValues = (updatedSelectedPromptOptionValues: SelectedPromptOptionValues): void => {
    setSelectedPromptOptionValues(updatedSelectedPromptOptionValues);
    props.onSelectedPromptOptionValuesChange?.(updatedSelectedPromptOptionValues);
  };

  const updateEditableComponentsMap = (map: EditableComponentsMap): void => {
    setEditableComponentsMap(map);
    props.onEditableComponentsMapChange?.(map);
  };

  const handleError = (error: RedesignIssue | undefined): void => {
    setRedesignIssue(error);
    props.onRedesignIssue?.(error);
  };

  const handleSubmission = async (): Promise<void> => {
    handleError(undefined);
    setIsPromptBoxSelected(false);
    setIsLoading(true);

    try {
      await props.regenerateContent(prompt, selectedPromptOptionValues, editableComponentsMap);
      handlePromptChange('');
    } catch (e) {
      if (e instanceof AiRedesignPromptBoxIssue) {
        handleError(e as RedesignIssue);
      } else {
        handleError({
          type: RedesignIssueType.ERROR,
          icon: 'icon-exclamation-triangle',
          message: window.i18next.t('pmwjs_error_occurred_retry'),
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getHeader = (): ReactElement | undefined => {
    if (!props.headerContent) {
      return;
    }

    return <PromptBoxHeader headerContent={props.headerContent} />;
  };

  const showinitialAnimation = (): ReactElement | undefined => {
    if (!props.showInitialAnimation) {
      return undefined;
    }

    return <div className={styles.initialAnimation}></div>;
  };

  const handleInputBoxClick = (): void => {
    setIsPromptBoxSelected(true);
  };

  const getPromptInputField = (): ReactElement => {
    return (
      <PromptInputField
        prompt={prompt}
        onPromptChange={handlePromptChange}
        isPromptBoxSelected={isPromptBoxSelected}
        placeholder={props.placeholder}
        handleSubmission={handleSubmission}
      />
    );
  };

  const getFooter = (): ReactElement => {
    const shouldShowControls = !isLoading && (isPromptBoxSelected || !!prompt);

    return (
      <InputBoxFooter
        showControls={shouldShowControls}
        isPromptBoxSelected={isPromptBoxSelected}
        isLoading={isLoading}
        isSubmitButtonActive={!!prompt}
        onSubmission={handleSubmission}
        creditsPillTheme={redesignIssue && redesignIssue.type === RedesignIssueType.WARNING ? CreditsPillTheme.WARNING : CreditsPillTheme.SECONDARY}
        promptOptionsToUse={promptOptionsToUse}
        selectedPromptOptionValues={selectedPromptOptionValues}
        onSelectedPromptOptionValuesChange={updateSelectedPromptOptionValues}
        editableComponentsMap={editableComponentsMap}
        onEditableComponentsMapChange={updateEditableComponentsMap}
        upsellDialog={props.upsellDialog}
        creditsPillContent={props.creditsPillContent}
        authenticateModalContent={props.authenticateModalContent}
      />
    );
  };

  const getInputBox = (): ReactElement => {
    return (
      <div
        className={`${styles.promptBox} radius-8 ${isPromptBoxSelected ? styles.active : ''} ${isLoading ? styles.promptBoxLoading : ''} ${redesignIssue && redesignIssue.type === RedesignIssueType.ERROR ? styles.redesignIssueError : ''} ${redesignIssue && redesignIssue.type === RedesignIssueType.WARNING ? styles.redesignIssueWarning : ''}`}
        onClick={handleInputBoxClick}
      >
        {showinitialAnimation()}
        {getPromptInputField()}
        {getFooter()}
      </div>
    );
  };

  const getRedesignIssue = (): ReactElement | undefined => {
    if (!redesignIssue) {
      return;
    }

    return <AiRedesignIssue redesignIssue={redesignIssue} />;
  };

  const getPromptSuggestions = (): ReactElement | undefined => {
    if (prompt) {
      return undefined;
    }

    const hasSuggestions = !!props.promptSuggestions || !!props.recentPrompt;
    const shouldShow = hasSuggestions && !redesignIssue && isPromptBoxSelected;

    if (!shouldShow) {
      return undefined;
    }

    return <AiRedesignPromptSuggestions promptSuggestions={props.promptSuggestions} recentPrompt={props.recentPrompt} handlePromptChange={handlePromptChange} />;
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    if (!unselectOnOutsideClick) {
      return;
    }

    const nextFocused = event.relatedTarget as HTMLElement | null;
    if (!promptBoxRef.current?.contains(nextFocused)) {
      setIsPromptBoxSelected(false);
    }
  };

  const getPromptBox = (): ReactElement => {
    return (
      <div>
        {getHeader()}
        <div ref={promptBoxRef} tabIndex={-1} onBlur={handleBlur} className={`${promptBoxClasses} ${isLoading ? styles.disabledInteraction : ''} ${styles.promptBoxContainer}`}>
          {getInputBox()}
          {getRedesignIssue()}
          <AnimatePresence initial={false}>{getPromptSuggestions()}</AnimatePresence>
        </div>
      </div>
    );
  };

  return getPromptBox();
}
